import React, { FC, useEffect, useMemo, useState } from "react";
import { DateValue, parseDate } from "@internationalized/date";
import { useTranslation } from "react-i18next";
import { StringParam, useQueryParam } from "use-query-params";
import { useNavigate } from "react-router-dom";
import { billingService } from "../../../api";
import { Button, DatePickerCustom, Select, Table, TopProgress } from "../../../components";
import { InfoContainer } from "../../../components/info-block/styles";
import { usePageTitle } from "../../../context/navigationContext";
import { useUi } from "../../../context/ui.context";
import { formatCurrency } from "../../../utils/display-price";
import {
  CardContent,
  CardsGrid,
  CardMeta,
  CardTitle,
  CardValue,
  CellLink,
  CellPrimary,
  Container,
  EmptyText,
  FiltersCard,
  HeaderRow,
  PageTitle,
  SectionCard,
  SectionHeader,
  SectionSubtitle,
  SectionTitle,
} from "./styles";

type BillingOverviewItem = {
  type: string;
  value: string | number;
};

type PaymentTypeOption = {
  value: string;
  label: string;
};

type BillingOverviewData = {
  items?: BillingOverviewItem[];
  start_date?: string;
  end_date?: string;
  total?: string | number;
  currency?: string;
  filter_options?: {
    payment_types?: PaymentTypeOption[];
  };
};

const pad = (value: number) => value.toString().padStart(2, "0");

const getDefaultStartDate = () => {
  const today = new Date();
  const date = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

const normalizeDateForPicker = (value?: string | null) => {
  if (!value) return null;
  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  const apiMatch = value.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
  if (apiMatch) return `${apiMatch[3]}-${apiMatch[2]}-${apiMatch[1]}`;
  return null;
};

const parseDateValue = (value?: string | null) => {
  const normalized = normalizeDateForPicker(value);
  if (!normalized) return undefined;
  try {
    return parseDate(normalized);
  } catch {
    return undefined;
  }
};

const formatDateValue = (value: DateValue) => `${pad(value.day)}.${pad(value.month)}.${value.year}`;

const formatDisplayDate = (value?: string | null) => {
  if (!value) return "-";
  const normalized = normalizeDateForPicker(value);
  if (!normalized) return value;
  const [year, month, day] = normalized.split("-");
  return `${day}.${month}.${year}`;
};

const getDisplayValue = (value: any): string => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "object") {
    return `${value.display || value.name || value.label || value.full_name || value.email || value.id || ""}`;
  }
  return `${value}`;
};

const getFirstValue = (...values: any[]) => {
  return values.find((value) => value !== null && value !== undefined && value !== "");
};

const getListFromResponse = (data: any) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.entries)) return data.entries;
  if (Array.isArray(data?.objects)) return data.objects;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

const BillingOverview: FC = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { viewAlert } = useUi();
  const defaultStartDate = useMemo(() => getDefaultStartDate(), []);
  const [startDate, setStartDate] = useQueryParam("start_date", StringParam);
  const [endDate, setEndDate] = useQueryParam("end_date", StringParam);
  const [paymentType, setPaymentType] = useQueryParam("payment_type", StringParam);
  const [overview, setOverview] = useState<BillingOverviewData | null>(null);
  const [journalRows, setJournalRows] = useState<any[]>([]);
  const [journalSummary, setJournalSummary] = useState<{ total?: string | number; currency?: string } | null>(null);
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);

  usePageTitle(t("PAGE_TITLE_BILLING_OVERVIEW"));

  const params = useMemo(() => ({
    start_date: startDate || defaultStartDate,
    end_date: endDate || undefined,
    payment_type: paymentType || undefined,
  }), [defaultStartDate, endDate, paymentType, startDate]);

  const paymentTypeOptions = useMemo(() => {
    const apiOptions = overview?.filter_options?.payment_types || [];
    const availableTypes = (overview?.items || [])
      .map((item) => item.type)
      .filter(Boolean);

    return [
      { id: "", value: "", label: t("ALL") },
      ...availableTypes.map((type) => {
        const apiOption = apiOptions.find((option) => option.label === type || option.value === type);
        const value = apiOption?.value || type;

        return {
          id: value,
          value,
          label: type,
        };
      }),
    ];
  }, [overview, t]);

  const paymentTypeLabels = useMemo(() => {
    return (overview?.filter_options?.payment_types || []).reduce((acc: Record<string, string>, option) => {
      acc[option.value] = option.label;
      return acc;
    }, {});
  }, [overview]);

  const formatMoney = (value: any, currency?: string) => {
    if (value === null || value === undefined || value === "") return "-";
    if (!currency) return `${value}`;
    return formatCurrency(value, currency as any);
  };

  const renderLinkedEntity = (entity: any, type: "client" | "user") => {
    const label = getDisplayValue(entity);
    if (!label) return null;
    if (!entity?.id) return <CellPrimary>{label}</CellPrimary>;

    const path = type === "client"
      ? `/clients-users/clients/${entity.id}`
      : `/clients-users/users/${entity.id}`;

    return (
      <CellLink
        onClick={(event: React.MouseEvent) => {
          event.stopPropagation();
          navigate(path);
        }}
      >
        {label}
      </CellLink>
    );
  };

  const getPaymentTypeLabel = (item: any, overviewData?: BillingOverviewData | null) => {
    const rawPaymentType = getFirstValue(
      item?.payment_type,
      item?.payment_type_code,
      item?.payment_type_display,
      item?.type,
    );
    const value = getDisplayValue(rawPaymentType);
    if (!value) return "-";
    const labels = overviewData?.filter_options?.payment_types?.reduce((acc: Record<string, string>, option) => {
      acc[option.value] = option.label;
      return acc;
    }, {}) || paymentTypeLabels;
    return labels[value] || value;
  };

  const mapJournalRows = (items: any[], overviewData: BillingOverviewData | null) => {
    return items.map((item, index) => {
      const amount = getFirstValue(item?.destination_amount, item?.amount, item?.value, item?.source_amount);
      const currency = getDisplayValue(getFirstValue(item?.destination_currency, item?.currency, overviewData?.currency));

      return {
        ...item,
        id: item?.id || `${index}-${getPaymentTypeLabel(item, overviewData)}-${amount || ""}`,
        type: getPaymentTypeLabel(item, overviewData),
        client: renderLinkedEntity(item?.client, "client") || "-",
        amount: formatMoney(amount, currency),
      };
    });
  };

  useEffect(() => {
    let cancelled = false;

    const loadData = async (showLoader: boolean) => {
      if (showLoader) setLoading(true);

      try {
        const [overviewResponse, journalsResponse] = await Promise.all([
          billingService.getBillingOverview(params),
          billingService.getBillingOverviewJournals(params),
        ]);

        if (cancelled) return;

        const overviewData = overviewResponse.data || null;
        const journalsData = journalsResponse.data || {};
        setOverview(overviewData);
        setJournalRows(mapJournalRows(getListFromResponse(journalsData), overviewData));
        setJournalSummary({
          total: journalsData?.total,
          currency: journalsData?.currency,
        });
      } catch (error: any) {
        if (cancelled) return;

        console.log("error", error);
        setJournalRows([]);
        setJournalSummary(null);
        if (showLoader && error?.response?.data?.detail) {
          viewAlert({
            severity: "error",
            message: error.response.data.detail,
          });
        }
      } finally {
        if (!cancelled) {
          setPageLoading(false);
          setLoading(false);
        }
      }
    };

    loadData(true);
    const interval = setInterval(() => loadData(false), 10000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [params.start_date, params.end_date, params.payment_type]);

  const resetFilters = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setPaymentType(undefined);
  };

  const columns = [
    { title: "ID", key: "id" },
    { title: t("TYPE"), key: "type" },
    { title: t("CLIENT"), key: "client" },
    { title: t("AMOUNT"), key: "amount" },
  ];

  const displayedStartDate = overview?.start_date || params.start_date;
  const displayedEndDate = overview?.end_date || params.end_date;
  const hasActiveFilters = Boolean(startDate || endDate || paymentType);

  return (
    <Container>
      <HeaderRow>
        <PageTitle>{t("BILLING_OVERVIEW")}</PageTitle>
      </HeaderRow>

      <FiltersCard>
        <DatePickerCustom
          title={String(t("START_DATE"))}
          value={parseDateValue(startDate || defaultStartDate)}
          onChange={(value) => setStartDate(value ? formatDateValue(value) : undefined)}
        />
        <DatePickerCustom
          title={String(t("END_DATE"))}
          value={parseDateValue(endDate)}
          onChange={(value) => setEndDate(value ? formatDateValue(value) : undefined)}
        />
        <Select
          title={t("PAYMENT_TYPE")}
          data={paymentTypeOptions}
          selectedValue={paymentType || ""}
          onChange={(value) => setPaymentType(value || undefined)}
          customStyles={`
            > p:last-child {
              display: none;
            }
          `}
        />
        <Button
          title={t("CLEAR_FILTERS")}
          onClick={resetFilters}
          variant="stroke"
          size="display"
          disabled={!hasActiveFilters}
        />
      </FiltersCard>

      {pageLoading ? <TopProgress loading={pageLoading} /> : null}

      {overview ? (
        <CardsGrid>
          <InfoContainer style={{ marginBottom: 0 }}>
            <CardContent>
              <CardTitle>{t("TOTAL")}</CardTitle>
              <CardValue>{formatMoney(overview.total, overview.currency)}</CardValue>
              <CardMeta>
                {formatDisplayDate(displayedStartDate)} - {formatDisplayDate(displayedEndDate)}
              </CardMeta>
            </CardContent>
          </InfoContainer>
          {(overview.items || []).map((item) => (
            <InfoContainer key={item.type} style={{ marginBottom: 0 }}>
              <CardContent>
                <CardTitle>{item.type}</CardTitle>
                <CardValue>{formatMoney(item.value, overview.currency)}</CardValue>
                <CardMeta>{t("PAYMENT_TYPE")}</CardMeta>
              </CardContent>
            </InfoContainer>
          ))}
        </CardsGrid>
      ) : !pageLoading ? (
        <SectionCard>
          <EmptyText>{t("NO_BILLING_OVERVIEW_DATA")}</EmptyText>
        </SectionCard>
      ) : null}

      <SectionCard>
        <SectionHeader>
          <SectionTitle>{t("JOURNAL")}</SectionTitle>
          <SectionSubtitle>
            {t("BILLING_OVERVIEW_JOURNALS_DESCRIPTION")}
            {journalSummary?.total !== null && journalSummary?.total !== undefined
              ? ` ${t("TOTAL")}: ${formatMoney(journalSummary.total, journalSummary.currency)}`
              : ""}
          </SectionSubtitle>
        </SectionHeader>
        {journalRows.length > 0 || loading ? (
          <Table
            columns={columns}
            rows={journalRows}
            hideCheckbox
            hideOther
            loading={loading && !pageLoading}
          />
        ) : (
          <EmptyText>{t("NO_JOURNAL_RECORDS")}</EmptyText>
        )}
      </SectionCard>
    </Container>
  );
};

export default BillingOverview;
